import { AppState } from '../domain/state/AppState';
import type { GameStateMachine } from '../domain/state/GameStateMachine';
import type { ExperienceScene, IntroSceneVisualDebug } from '../game/scenes/ExperienceScene';

let activeControllers = 0;
let activeScrollListeners = 0;
let activeWheelListeners = 0;

export type ScrollDebugSnapshot = {
  windowScrollY: number;
  documentScrollTop: number;
  bodyScrollTop: number;
  activeScrollContainer: string;
  scrollContainerTop: number;
  scrollContainerHeight: number;
  scrollContainerClientHeight: number;
  progress: number;
  isActive: boolean;
  wheelReceived: boolean;
  wheelDeltaX: number;
  wheelDeltaY: number;
  wheelDeltaMode: number;
  wheelCancelable: boolean;
  wheelDefaultPrevented: boolean;
  wheelTarget: string;
  wheelCurrentTarget: string;
  wheelPath: string;
  wheelScrollYBefore: number;
  wheelScrollYAfter: number;
  state: AppState;
  bodyOverflow: string;
  htmlOverflow: string;
  appOverflow: string;
  stageOverflow: string;
  canvasPointerEvents: string;
  activeScrollListeners: number;
  activeWheelListeners: number;
  activeGsapObservers: number;
  activeScrollTriggers: number;
  bodyScrollLocked: boolean;
  scrollHeight: number;
  viewportHeight: number;
  sectionTop: number;
  scrollDistance: number;
  stageTop: number;
  stagePosition: string;
  stageTransform: string;
  readyPromptVisible: boolean;
  readyPromptAlpha: number;
  scene: IntroSceneVisualDebug;
};

const READY_THRESHOLD = 0.985;
const READY_EXIT_THRESHOLD = 0.98;

export class IntroTimeline {
  private section?: HTMLElement;
  private observer?: ResizeObserver;
  private updateFrame?: number;
  private debugElement?: HTMLPreElement;
  private currentProgress = 0;
  private wheelReceived = false;
  private wheelDeltaX = 0;
  private wheelDeltaY = 0;
  private wheelDeltaMode = 0;
  private wheelCancelable = false;
  private wheelDefaultPrevented = false;
  private wheelTarget = 'none';
  private wheelCurrentTarget = 'none';
  private wheelPath = 'none';
  private wheelScrollYBefore = 0;
  private wheelScrollYAfter = 0;
  private scrollListenerActive = false;
  private wheelListenerActive = false;
  private active = false;

  public constructor(
    private readonly scene: ExperienceScene,
    private readonly machine: GameStateMachine,
    private readonly debugEnabled = false,
  ) {}

  public create(): void {
    this.destroy();
    const section = document.querySelector<HTMLElement>('#experience');
    if (!section) throw new Error('Intro scroll section is missing.');
    this.section = section;
    this.active = true;
    activeControllers += 1;
    window.addEventListener('scroll', this.queueUpdate, { passive: true });
    this.scrollListenerActive = true;
    activeScrollListeners += 1;
    window.addEventListener('resize', this.queueUpdate, { passive: true });
    window.addEventListener('orientationchange', this.queueUpdate, { passive: true });
    if (this.debugEnabled) {
      window.addEventListener('wheel', this.captureWheel, { capture: true, passive: true });
      this.wheelListenerActive = true;
      activeWheelListeners += 1;
      this.createDebugOverlay();
    }
    this.observer = new ResizeObserver(this.queueUpdate);
    this.observer.observe(section);
    const stage = document.querySelector<HTMLElement>('#game-shell');
    if (stage) this.observer.observe(stage);
    this.currentProgress = 0;
    this.applyProgress(0);
    this.queueUpdate();
    void document.fonts.ready.then(this.queueUpdate);
  }

  public destroy(): void {
    window.removeEventListener('scroll', this.queueUpdate);
    window.removeEventListener('resize', this.queueUpdate);
    window.removeEventListener('orientationchange', this.queueUpdate);
    window.removeEventListener('wheel', this.captureWheel, true);
    this.observer?.disconnect();
    if (this.updateFrame !== undefined) cancelAnimationFrame(this.updateFrame);
    this.debugElement?.remove();
    if (this.scrollListenerActive) {
      activeScrollListeners = Math.max(0, activeScrollListeners - 1);
    }
    if (this.wheelListenerActive) {
      activeWheelListeners = Math.max(0, activeWheelListeners - 1);
    }
    if (this.active) activeControllers = Math.max(0, activeControllers - 1);
    this.section = undefined;
    this.observer = undefined;
    this.updateFrame = undefined;
    this.debugElement = undefined;
    this.scrollListenerActive = false;
    this.wheelListenerActive = false;
    this.active = false;
  }

  public refresh(): void {
    this.queueUpdate();
  }

  public get activeTriggerCount(): number {
    return 0;
  }

  public get activeControllerCount(): number {
    return activeControllers;
  }

  public get debugSnapshot(): ScrollDebugSnapshot {
    const sectionTop = this.section?.getBoundingClientRect().top ?? 0;
    const stage = document.querySelector<HTMLElement>('#game-shell');
    const stageStyle = stage ? getComputedStyle(stage) : undefined;
    const app = document.querySelector<HTMLElement>('#app');
    const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas-container canvas');
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    const readyPrompt = document.querySelector<HTMLElement>('#ready');
    const readyStyle = readyPrompt ? getComputedStyle(readyPrompt) : undefined;
    return {
      windowScrollY: window.scrollY,
      documentScrollTop: document.documentElement.scrollTop,
      bodyScrollTop: document.body.scrollTop,
      activeScrollContainer: describeElement(scrollingElement),
      scrollContainerTop: scrollingElement.scrollTop,
      scrollContainerHeight: scrollingElement.scrollHeight,
      scrollContainerClientHeight: scrollingElement.clientHeight,
      progress: this.currentProgress,
      isActive: this.active,
      wheelReceived: this.wheelReceived,
      wheelDeltaX: this.wheelDeltaX,
      wheelDeltaY: this.wheelDeltaY,
      wheelDeltaMode: this.wheelDeltaMode,
      wheelCancelable: this.wheelCancelable,
      wheelDefaultPrevented: this.wheelDefaultPrevented,
      wheelTarget: this.wheelTarget,
      wheelCurrentTarget: this.wheelCurrentTarget,
      wheelPath: this.wheelPath,
      wheelScrollYBefore: this.wheelScrollYBefore,
      wheelScrollYAfter: this.wheelScrollYAfter,
      state: this.machine.state,
      bodyOverflow: getComputedStyle(document.body).overflowY,
      htmlOverflow: getComputedStyle(document.documentElement).overflowY,
      appOverflow: app ? getComputedStyle(app).overflowY : 'missing',
      stageOverflow: stageStyle?.overflowY ?? 'missing',
      canvasPointerEvents: canvas ? getComputedStyle(canvas).pointerEvents : 'missing',
      activeScrollListeners,
      activeWheelListeners,
      activeGsapObservers: 0,
      activeScrollTriggers: 0,
      bodyScrollLocked:
        document.body.classList.contains('game-active') ||
        document.documentElement.classList.contains('game-active') ||
        getComputedStyle(document.body).overflowY === 'hidden' ||
        getComputedStyle(document.documentElement).overflowY === 'hidden',
      scrollHeight: document.documentElement.scrollHeight,
      viewportHeight: window.innerHeight,
      sectionTop,
      scrollDistance: Math.max(0, (this.section?.offsetHeight ?? 0) - window.innerHeight),
      stageTop: stage?.getBoundingClientRect().top ?? 0,
      stagePosition: stageStyle?.position ?? 'missing',
      stageTransform: stageStyle?.transform ?? 'missing',
      readyPromptVisible:
        readyStyle?.visibility === 'visible' &&
        readyStyle.display !== 'none' &&
        readyStyle.opacity !== '0',
      readyPromptAlpha: Number.parseFloat(readyStyle?.opacity ?? '0'),
      scene: this.scene.introVisualDebug,
    };
  }

  private readonly queueUpdate = (): void => {
    if (this.updateFrame !== undefined) return;
    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = undefined;
      this.updateFromScrollPosition();
    });
  };

  private readonly captureWheel = (event: WheelEvent): void => {
    this.wheelReceived = true;
    this.wheelDeltaX = event.deltaX;
    this.wheelDeltaY = event.deltaY;
    this.wheelDeltaMode = event.deltaMode;
    this.wheelCancelable = event.cancelable;
    this.wheelDefaultPrevented = event.defaultPrevented;
    this.wheelTarget = describeElement(event.target);
    this.wheelCurrentTarget = describeElement(event.currentTarget);
    this.wheelPath = event.composedPath().slice(0, 8).map(describeElement).join(' > ');
    this.wheelScrollYBefore = window.scrollY;
    this.wheelScrollYAfter = window.scrollY;
    this.renderDebugOverlay();
    requestAnimationFrame(() => {
      this.wheelDefaultPrevented = event.defaultPrevented;
      this.wheelScrollYAfter = window.scrollY;
      this.renderDebugOverlay();
    });
  };

  private updateFromScrollPosition(): void {
    if (!this.section) return;
    const scrollDistance = this.section.offsetHeight - window.innerHeight;
    const travelled = Math.min(
      Math.max(-this.section.getBoundingClientRect().top, 0),
      scrollDistance,
    );
    const progress = scrollDistance <= 0 ? 0 : travelled / scrollDistance;
    this.currentProgress = Math.min(1, Math.max(0, progress));
    this.applyProgress(this.currentProgress);
    this.renderDebugOverlay();
  }

  private applyProgress(progress: number): void {
    this.scene.renderIntroProgress(progress);
    if (progress >= READY_THRESHOLD && this.machine.state === AppState.Intro) {
      this.machine.transition(AppState.Ready);
    } else if (progress < READY_EXIT_THRESHOLD && this.machine.state === AppState.Ready) {
      this.machine.transition(AppState.Intro);
    }
  }

  private createDebugOverlay(): void {
    this.debugElement = document.createElement('pre');
    this.debugElement.id = 'scroll-debug';
    this.debugElement.setAttribute('aria-hidden', 'true');
    document.body.append(this.debugElement);
    this.renderDebugOverlay();
  }

  private renderDebugOverlay(): void {
    if (!this.debugElement) return;
    const data = this.debugSnapshot;
    this.debugElement.textContent = [
      `window.scrollY: ${data.windowScrollY.toFixed(2)}`,
      `document.scrollTop: ${data.documentScrollTop.toFixed(2)}`,
      `body.scrollTop: ${data.bodyScrollTop.toFixed(2)}`,
      `scroll owner: ${data.activeScrollContainer}`,
      `container top/height/client: ${data.scrollContainerTop.toFixed(2)} / ${data.scrollContainerHeight} / ${data.scrollContainerClientHeight}`,
      `native progress: ${data.progress.toFixed(4)}`,
      `section top/distance: ${data.sectionTop.toFixed(2)} / ${data.scrollDistance.toFixed(2)}`,
      `stage top/position: ${data.stageTop.toFixed(2)} / ${data.stagePosition}`,
      `stage transform: ${data.stageTransform}`,
      `listeners scroll/wheel: ${data.activeScrollListeners} / ${data.activeWheelListeners}`,
      `GSAP observers/triggers: ${data.activeGsapObservers} / ${data.activeScrollTriggers}`,
      `controller active: ${String(data.isActive)}`,
      `wheel received: ${String(data.wheelReceived)}`,
      `wheel deltaX/deltaY/mode: ${data.wheelDeltaX.toFixed(2)} / ${data.wheelDeltaY.toFixed(2)} / ${data.wheelDeltaMode}`,
      `wheel cancelable/defaultPrevented: ${String(data.wheelCancelable)} / ${String(data.wheelDefaultPrevented)}`,
      `wheel target/currentTarget: ${data.wheelTarget} / ${data.wheelCurrentTarget}`,
      `wheel path: ${data.wheelPath}`,
      `wheel scrollY before/after: ${data.wheelScrollYBefore.toFixed(2)} / ${data.wheelScrollYAfter.toFixed(2)}`,
      `state: ${data.state}`,
      `body/html overflowY: ${data.bodyOverflow} / ${data.htmlOverflow}`,
      `app/stage overflowY: ${data.appOverflow} / ${data.stageOverflow}`,
      `canvas pointer-events: ${data.canvasPointerEvents}`,
      `body scroll locked: ${String(data.bodyScrollLocked)}`,
      `scrollHeight/viewport: ${data.scrollHeight} / ${data.viewportHeight}`,
      `controllers: ${activeControllers}`,
      `ship x/y: ${data.scene.ship.x.toFixed(1)} / ${data.scene.ship.y.toFixed(1)}`,
      `ship visible/active/alpha/depth: ${String(data.scene.ship.visible)} / ${String(data.scene.ship.active)} / ${data.scene.ship.alpha.toFixed(2)} / ${data.scene.ship.depth}`,
      `ground y/visible/alpha/depth: ${data.scene.ground.y.toFixed(1)} / ${String(data.scene.ground.visible)} / ${data.scene.ground.alpha.toFixed(2)} / ${data.scene.ground.depth}`,
      `space chunks/moon: ${data.scene.spaceBackground.visibleChunkCount} / ${String(data.scene.spaceBackground.moonVisible)}`,
      `ready visible/alpha: ${String(data.readyPromptVisible)} / ${data.readyPromptAlpha.toFixed(2)}`,
      `camera scroll x/y: ${data.scene.camera.scrollX.toFixed(1)} / ${data.scene.camera.scrollY.toFixed(1)}`,
    ].join('\n');
  }
}

function describeElement(value: EventTarget | null): string {
  if (value === window) return 'window';
  if (value === document) return '#document';
  if (!(value instanceof Element)) return value ? value.constructor.name : 'none';
  const id = value.id ? `#${value.id}` : '';
  const classes = [...value.classList].map((name) => `.${name}`).join('');
  return `${value.tagName.toLowerCase()}${id}${classes}`;
}
